import { isDefined } from "@clipboard-health/util-ts";

import { getShiftWorkDurationInHours } from "../../utils/getShiftWorkDuration";
import { MANDATORY_BREAK_DURATION_IN_MINUTES } from "../constants";
import { type DoubleShiftPay, type ShiftDouble } from "./types";

export interface ShiftTimeRange {
  start: string;
  end: string;
}

export interface OfferPay {
  amountInMinorUnits: number;
}

interface ComputeDoubleShiftPaysParams {
  double: ShiftDouble;
  anchorShift: ShiftTimeRange;
  anchorOffer?: { attributes: { pay: OfferPay } };
  offersMap: Map<string, { attributes: { pay: OfferPay } }>;
  requiresLunchBreak: boolean;
}

/**
 * Computes pay for a single shift given its time range and hourly rate.
 * Shared by both double and bundle pay calculations.
 */
export function computeShiftPay(
  shift: ShiftTimeRange,
  hourlyPayInMinorUnits: number,
  requiresLunchBreak: boolean
): DoubleShiftPay {
  return {
    totalPayInMinorUnits: Math.round(
      getShiftWorkDurationInHours(shift, requiresLunchBreak, MANDATORY_BREAK_DURATION_IN_MINUTES) *
        hourlyPayInMinorUnits
    ),
    hourlyPayInMinorUnits,
  };
}

/**
 * Computes pay data for all shifts in a double (anchor + companions).
 * Returns the double enriched with anchorShiftPay and companionShiftPays.
 */
export function computeDoubleShiftPays(
  params: ComputeDoubleShiftPaysParams
): Pick<ShiftDouble, "anchorShiftPay" | "companionShiftPays"> {
  const { double, anchorShift, anchorOffer, offersMap, requiresLunchBreak } = params;

  const anchorShiftPay = isDefined(anchorOffer)
    ? computeShiftPay(
        anchorShift,
        anchorOffer.attributes.pay.amountInMinorUnits,
        requiresLunchBreak
      )
    : undefined;

  const companionShiftPays = new Map<string, DoubleShiftPay>();
  for (const companion of double.companionShifts) {
    const companionOfferId = companion.relationships.offer?.data.id;
    const companionOffer = companionOfferId ? offersMap.get(companionOfferId) : undefined;
    if (isDefined(companionOffer)) {
      companionShiftPays.set(
        companion.id,
        computeShiftPay(
          { start: companion.attributes.start, end: companion.attributes.end },
          companionOffer.attributes.pay.amountInMinorUnits,
          requiresLunchBreak
        )
      );
    }
  }

  return { anchorShiftPay, companionShiftPays };
}
